import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@/features/auth/auth-context'
import { testHistoryQueryOptions } from '@/features/history/history-api'
import { dashboardApi } from '@/lib/api'
import { demoResults } from '@/lib/demo-data'
import type { StudentDashboardStats, TestResult } from '@/lib/types'
import { isShowableBand } from '@/domain/results/band-display'
import { latestMock } from './home-model'

/**
 * The demo session's stats (Home and the top bar's streak pill read the same numbers), worked out from `demoResults`: the
 * same rows the week strip, Recent results and the latest-mock card draw. So the streak matches the ticked days, "tests
 * this week" counts the attempts in the strip's seven days, and the skill bands are the latest full mock's. Nothing is
 * written down twice, so nothing on Home can disagree with itself.
 */
export function demoStatsAt(now: Date = new Date()): StudentDashboardStats {
  const rows = demoResults.filter((row) => row.status !== 'abandoned')
  const days = practisedDays(rows)
  const dayKey = (date: Date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
  const back = (offset: number) => new Date(now.getFullYear(), now.getMonth(), now.getDate() - offset)
  // A streak counts back from today, or from yesterday while today has no attempt yet.
  let streak = 0
  for (let offset = days.has(dayKey(now)) ? 0 : 1; days.has(dayKey(back(offset))); offset += 1) streak += 1
  const weekStart = back(6).getTime()
  const thisWeek = rows.filter((row) => {
    const time = Date.parse(row.completed_at)
    return !Number.isNaN(time) && time >= weekStart && time <= now.getTime() + 86_400_000
  })
  const seconds = (list: typeof rows) => list.reduce((sum, row) => sum + (Number(row.time_spent) || 0), 0)
  const mock = latestMock(demoResults)
  const banded = rows.filter((row) => isShowableBand((row.section_scores as { overall_band?: number | null } | undefined)?.overall_band))
  return {
    testsCompleted: rows.length,
    testsCompletedThisWeek: thisWeek.length,
    studyHours: Math.round(seconds(rows) / 360) / 10,
    studyHoursThisWeek: Math.round(seconds(thisWeek) / 360) / 10,
    currentStreak: streak,
    averageScore: mock?.status.band ?? 0,
    averageScoreChange: 0,
    bandedAttempts: banded.length,
    latestSectionBands: {
      listening: mock?.bands.listening ?? null,
      reading: mock?.bands.reading ?? null,
      writing: mock?.bands.writing ?? null,
      speaking: mock?.bands.speaking ?? null,
    },
  }
}

export const STATS_QUERY_KEY = ['dashboard', 'stats'] as const
const PRACTICE_ROWS = 20

/** The student's dashboard stats: the same query Home uses (one cache entry). The demo session reads the sample. */
export function useStudentStats() {
  const { demo } = useAuth()
  const query = useQuery({ queryKey: STATS_QUERY_KEY, queryFn: dashboardApi.stats, enabled: !demo })
  return { stats: demo ? demoStatsAt() : query.data, query }
}

/** Local calendar days (yyyy-mm-dd) with a completed attempt, from rows the page already has. */
export function practisedDays(rows: readonly Pick<TestResult, 'completed_at' | 'status'>[]): Set<string> {
  const days = new Set<string>()
  for (const row of rows) {
    if (row.status === 'abandoned') continue
    const time = Date.parse(row.completed_at)
    if (Number.isNaN(time)) continue
    const date = new Date(time)
    days.add(`${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`)
  }
  return days
}

/** Days practised, from the first page of completed attempts (read only when `enabled`: the streak card is open, or Home). */
export function usePractisedDays(enabled = true) {
  const { demo } = useAuth()
  const query = useQuery({ ...testHistoryQueryOptions(PRACTICE_ROWS, 0, { status: 'completed' }), enabled: !demo && enabled })
  const rows = demo ? demoResults : query.data?.results
  return rows ? practisedDays(rows) : null
}
